import React, { useState, useCallback } from 'react';
import type { ContainerStatus, ContainerAction } from '../types';
import { useContainerDashboard } from '../hooks/useContainerDashboard';
import { useBatchUpgrade } from '../hooks/useBatchUpgrade';
import { ContainerStatusTable } from './ContainerStatusTable';
import { ContainerLogsModal } from './ContainerLogsModal';
import { BatchUpgradeModal } from './BatchUpgradeModal';
import { fetchVersions } from '../../upgrades/api/upgradeApi';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface ContainerDashboardPanelProps {
  authFetch: AuthFetch;
}

type StatusFilter = 'all' | ContainerStatus['status'];

export const ContainerDashboardPanel: React.FC<ContainerDashboardPanelProps> = ({
  authFetch,
}) => {
  const dashboard = useContainerDashboard(authFetch);
  const batch = useBatchUpgrade(authFetch);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [logsContainer, setLogsContainer] = useState<ContainerStatus | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchVersions, setBatchVersions] = useState<string[]>([]);

  const filtered =
    statusFilter === 'all'
      ? dashboard.containers
      : dashboard.containers.filter((c) => c.status === statusFilter);

  const handleAction = useCallback(
    (containerId: string, action: ContainerAction) => {
      dashboard.performAction(containerId, action);
    },
    [dashboard]
  );

  const handleBulkAction = useCallback(
    (action: ContainerAction) => {
      if (selectedIds.size === 0) return;
      dashboard.bulkAction(action, Array.from(selectedIds));
      setSelectedIds(new Set());
    },
    [dashboard, selectedIds]
  );

  const handleBatchStart = useCallback(
    async (tenantIds: string[], targetVersion: string, parallelLimit: number) => {
      await batch.initiateBatch({ tenant_ids: tenantIds, target_version: targetVersion, batch_size: parallelLimit });
    },
    [batch]
  );

  const error = dashboard.error || batch.error;
  const { stats } = dashboard;

  const STAT_CARDS: Array<{ label: string; value: number | string; className?: string }> = stats
    ? [
        { label: 'Total', value: stats.total_containers },
        { label: 'Running', value: stats.running, className: 'status-active' },
        { label: 'Stopped', value: stats.stopped, className: 'status-suspended' },
        { label: 'Unhealthy', value: stats.unhealthy, className: 'status-suspended' },
      ]
    : [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700 }}>Container Operations</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-sm btn-secondary" onClick={() => handleBulkAction('start')}>
            Start All
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => handleBulkAction('stop')}>
            Stop All
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => dashboard.refresh()}>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px' }}>
          {error}
          <button
            className="btn btn-sm btn-ghost"
            style={{ marginLeft: '8px' }}
            onClick={() => {
              dashboard.clearError();
              batch.clearError();
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {stats && (
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}
          aria-label="Container statistics"
        >
          {STAT_CARDS.map((card) => (
            <div key={card.label} className="glass-card" style={{ padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '13px', opacity: 0.7, marginBottom: '4px' }}>{card.label}</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>
                {card.className ? (
                  <span className={`status-badge ${card.className}`}>{card.value}</span>
                ) : (
                  card.value
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {stats && (
        <div
          className="glass-card"
          style={{ padding: '12px 16px', marginBottom: '16px', display: 'flex', gap: '24px', fontSize: '13px' }}
        >
          <span>CPU: {stats.total_cpu_percent.toFixed(1)}%</span>
          <span>Memory: {stats.total_memory_mb} MB</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '13px', opacity: 0.7 }}>Filter:</label>
        {(['all', 'running', 'stopped', 'unhealthy', 'not_provisioned'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setStatusFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}

        {selectedIds.size > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', opacity: 0.7 }}>{selectedIds.size} selected:</span>
            <button className="btn btn-sm btn-secondary" onClick={() => handleBulkAction('start')}>Start</button>
            <button className="btn btn-sm btn-secondary" onClick={() => handleBulkAction('stop')}>Stop</button>
            <button className="btn btn-sm btn-secondary" onClick={() => handleBulkAction('restart')}>Restart</button>
          </div>
        )}
      </div>

      {dashboard.isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', opacity: 0.5 }}>Loading containers...</div>
      ) : (
        <ContainerStatusTable
          containers={filtered}
          onAction={handleAction}
          onViewLogs={setLogsContainer}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}

      <div className="glass-card" style={{ padding: '16px', marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>Batch Upgrade</div>
          <div style={{ fontSize: '13px', opacity: 0.7 }}>
            {batch.isRunning
              ? `Batch in progress (${batch.progress?.completed ?? 0}/${batch.progress?.total ?? 0})`
              : 'No batch upgrade running'}
          </div>
        </div>
        <button
          className="btn btn-sm btn-primary"
          onClick={async () => {
            try {
              const data = await fetchVersions();
              const versions = data.versions
                .map((v) => v.version)
                .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
              setBatchVersions(versions);
            } catch {
              setBatchVersions([]);
            }
            setShowBatchModal(true);
          }}
        >
          {batch.isRunning ? 'View Progress' : 'Start Batch'}
        </button>
      </div>

      {logsContainer && (
        <ContainerLogsModal
          container={logsContainer}
          authFetch={authFetch}
          onClose={() => setLogsContainer(null)}
        />
      )}

      {showBatchModal && (
        <BatchUpgradeModal
          containers={dashboard.containers}
          availableVersions={batchVersions}
          onClose={() => setShowBatchModal(false)}
          onStart={handleBatchStart}
          onCancel={batch.cancelBatch}
          progress={batch.progress}
          isRunning={batch.isRunning}
        />
      )}
    </div>
  );
};
